import Vue from 'vue';
import { ValidationProvider, ValidationObserver, extend } from 'vee-validate';
import { required, min, max, email } from 'vee-validate/dist/rules';
import { mimeTypes } from '~/src/config/files';
import { parseNumber } from '~/src/utils/number';

const countFilesByType = (files, fileType) => (files ? [...files.entries()]
    .reduce((totalCount, [, { type }]) => totalCount + (mimeTypes[type] && mimeTypes[type].type === fileType ? 1 : 0), 0) : false);

extend('email', {
    ...email,
    message: 'Proszę wprowadzić poprawny adres email np. jan.kowalski@domena.pl.',
});

extend('required', {
    ...required,
    message: 'To pole jest wymagane.',
});

extend('one-picked', {
    validate: value => (value !== '' && value !== undefined && value !== null),
    message: 'Wybierz jedną z opcji.',
    computesRequired: true,
});

extend('checked', {
    validate: value => value,
    message: 'To pole jest wymagane.',
    computesRequired: true,
});

extend('regon', {
    validate: (value) => {
        if (value.length !== 9) {
            return false;
        }

        const arrSteps = [8, 9, 2, 3, 4, 5, 6, 7];
        let intSum = 0;

        for (let index = 0; index < 8; index += 1) {
            intSum += arrSteps[index] * value[index];
        }

        const int = intSum % 11;
        const intControlNr = (int === 10) ? 0 : int;

        return intControlNr === parseInt(value[8], 10);
    },
    message: 'Proszę wprowadź poprawny REGON w 9-cyfrowym formacie.',
});

extend('password', {
    validate: value => [/.{8}/, /[A-Z]/, /[a-z]/, /[!@#$%^&*)(+=._-]+|[0-9]+/, /^\S+$/].every(test => new RegExp(test).test(value)),
    message: 'Hasło powinno składać się co najmniej z 8 znaków: dużej litery, małej litery, cyfry lub znaku specjalnego (!\'^£$%&*()}{@#~?><>,|=_+¬-]).',
    computesRequired: true,
});

extend('min', {
    ...min,
    message: 'Minimalna ilość znaków to {length}.',
});

extend('max', {
    ...max,
    message: 'Maksymalna ilość znaków to {length}.',
});

extend('bigLetter', {
    validate: value => new RegExp(/[A-Z]/).test(value),
    message: 'Nie podano dużej litery.',
});

extend('smallLetter', {
    validate: value => new RegExp(/[a-z]/).test(value),
    message: 'Nie podano małej litery.',
});

extend('specialOrDigit', {
    validate: value => new RegExp(/[!@#$%^&*)(+=._-]+|[0-9]+/).test(value),
    message: 'Nie podano cyfry lub znaku specjalnego.',
});

extend('email-polish-chars', {
    validate: value => !/[żźćńółęąśŻŹĆĄŚĘŁÓŃ].*@/.test(value),
    message: 'Polskie znaki dozwolone tylko w części domenowej.',
});

extend('phone-number', {
    validate: value => new RegExp(/^\d{9}$/).test(value),
    message: 'Wprowadź numer w formacie 9-cio cyfrowym.',
});

extend('validate-postcode', {
    validate: value => new RegExp(/^\d{2}-\d{3}$/).test(value),
    message: 'Proszę podać kod pocztowy w prawidłowym formacie, np. 12-345.',
});

extend('is-date', {
    // eslint-disable-next-line no-restricted-globals
    validate: value => !isNaN(new Date(value)),
    message: 'Proszę wprowadzić poprawną datę.',
});

extend('is-date-late', {
    validate: (value) => {
        const currentDate = new Date().setHours(0, 0, 0, 0);
        const inputDate = new Date(value);

        return inputDate.getTime() >= currentDate;
    },
    message: 'Proszę wprowadź późniejszą datę niż data dzisiejsza.',
});

extend('is-date-later-than', {
    validate: (value, { earlierDate }) => {
        const currentTime = new Date(value).getTime();
        const earlierTime = new Date(earlierDate).getTime();

        return currentTime > earlierTime;
    },
    message: 'Proszę wybrać datę późniejszą niż data rozpoczęcia.',
    params: ['earlierDate'],
});

extend('greater-than-0', {
    validate: (value) => {
        const parsedValue = parseNumber(value);

        return !Number.isNaN(parsedValue) && parsedValue > 0;
    },
    message: 'Wprowadź liczbę większą niż 0.',
});

extend('only-digits', {
    validate: value => !new RegExp(/[^\d]/).test(value),
    message: 'Proszę używać tylko liczb w danym polu. Należy unikać także spacji oraz znaków specjalnych, jak kropki i przecinki.',
});

extend('min-images', {
    validate: (value, { number }) => countFilesByType(value, 'image') >= number,
    message: 'Minimalna ilość zdjęć to {number}.',
    computesRequired: true,
    params: ['number'],
});

extend('min-integers', {
    validate: (value, { number }) => parseInt(value, 10).toString().length >= number,
    message: 'Minimalna ilość cyfr przed przecinkiem to {number}.',
    params: ['number'],
});

extend('max-integers', {
    validate: (value, { number }) => parseInt(value, 10).toString().length <= number,
    message: 'Maksymalna ilość cyfr przed przecinkiem to {number}.',
    params: ['number'],
});

extend('min-video', {
    validate: (value, { number }) => countFilesByType(value, 'video') >= number,
    message: 'Minimalna ilość wideo to {number}.',
    computesRequired: true,
    params: ['number'],
});

extend('max-size-MB', {
    validate: (value, { size }) => [...value.entries()].reduce((totalSize, [, { size: fileSize }]) => totalSize + fileSize, 0) / 1024 / 1024 < size,
    message: 'Suma rozmiarów przesyłanych plików to maksymalnie: {size}',
    computesRequired: true,
    params: ['size'],
});

extend('allowed-formats', {
    validate: (value, { formats }) => [...value.entries()].every(([, { type }]) => mimeTypes[type] && formats.includes(mimeTypes[type].ext)),
    message: (ignore, { formats }) => `Dozwolone formaty to ${formats.join(', ')}.`,
    computesRequired: true,
    params: ['formats'],
});

Vue.component('ValidationProvider', ValidationProvider);
Vue.component('ValidationObserver', ValidationObserver);
